import React, { useState } from 'react';
import { UserIcon, BuildingOfficeIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import ImageModal from '../../components/ImageModal';

import { getImageUrlWithEnv } from '../../utils/imageUrl';
const Administration = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const management = [
    {
      name: "ვლადიმერ ქალიაშვილი",
      position: "პრეზიდენტი",
      department: "ხელმძღვანელობა",
      email: "president@fciveria.ge",
      photo_url: "/president.JPG",
      icon: UserIcon,
      color: "bg-blue-500"
    },
    {
      name: "ივანე ხარაზიშვილი",
      position: "დირექტორი",
      department: "ადმინისტრაცია",
      email: "director@fciveria.ge",
      photo_url: "/director.JPG",
      icon: BuildingOfficeIcon,
      color: "bg-green-500"
    },
    {
      name: "შალვა ღონღაძე",
      position: "ლიცენზიის მენეჯერი",
      department: "ლიცენზირება",
      email: "license@fciveria.ge",
      photo_url: "/license.JPG",
      icon: AcademicCapIcon,
      color: "bg-purple-500"
    }
  ];

  const departments = [
    {
      name: "ფინანსური დეპარტამენტი",
      head: "მარინა მჭედლიძე",
      email: "finance@fciveria.ge",
      responsibilities: [
        "ბიუჯეტის დაგეგმვა",
        "ფინანსური ანგარიშგება",
        "კონტრაქტების მართვა",
        "აუდიტი"
      ]
    },
    {
      name: "მედია დეპარტამენტი",
      head: "დიმიტრი ზავარინი",
      email: "media@fciveria.ge",
      responsibilities: [
        "მედია ურთიერთობები",
        "სოციალური ქსელები",
        "ღონისძიებების ორგანიზება",
        "კომუნიკაცია"
      ]
    },
    {
      name: "უსაფრთხოების დეპარტამენტი",
      head: "მერაბი ჩხეიძე",
      email: "security@fciveria.ge",
      responsibilities: [
        "უსაფრთხოების უზრუნველყოფა",
        "ღონისძიებების უსაფრთხოება",
        "პერსონალის დაცვა",
        "სტადიონის უსაფრთხოება"
      ]
    },
    {
      name: "გულშემატკივართა მომსახურება",
      head: "გიორგი აბელიანი",
      email: "fans@fciveria.ge",
      responsibilities: [
        "გულშემატკივართა მომსახურება",
        "შშმ პირთა მომსახურება",
        "ბილეთების გაყიდვა",
        "ღონისძიებების ორგანიზება"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            ადმინისტრაცია
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            კლუბის ხელმძღვანელობა და ადმინისტრაციული სტრუქტურა
          </p>
        </div>

        {/* Management Team */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            ხელმძღვანელობა
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
            {management.map((person, index) => (
              <div key={index} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105">
                {/* Person Photo */}
                <div className="relative h-48 bg-gray-200 cursor-pointer" onClick={() => person.photo_url && setSelectedImage({
                  imageUrl: getImageUrlWithEnv(person.photo_url),
                  alt: person.name,
                  title: person.name,
                  subtitle: `${person.position} • ${person.department}`
                })}>
                  {person.photo_url ? (
                    <img
                      src={getImageUrlWithEnv(person.photo_url)}
                      alt={person.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full ${person.photo_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700`}
                  >
                    <person.icon className="h-16 w-16 text-white" />
                  </div>
                </div>
                
                {/* Person Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 font-georgian text-lg mb-1">{person.name}</h3>
                  <p className="text-sm text-primary-600 font-georgian mb-1">{person.position}</p>
                  <p className="text-xs text-gray-500 font-georgian mb-3">{person.department}</p>
                  
                  <div className="space-y-1 text-sm text-gray-600 font-georgian">
                    <p className="text-xs">{person.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Departments */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            დეპარტამენტები
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {departments.map((dept, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-3">
                  {dept.name}
                </h3>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 font-georgian mb-1">
                    <span className="font-medium">ხელმძღვანელი:</span> {dept.head}
                  </p>
                  <p className="text-sm text-gray-600 font-georgian">
                    <span className="font-medium">ელ. ფოსტა:</span> {dept.email}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 font-georgian mb-2">
                    მოვალეობები:
                  </h4>
                  <ul className="space-y-1">
                    {dept.responsibilities.map((resp, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600 font-georgian">
                        <span className="w-1.5 h-1.5 bg-primary-600 rounded-full mr-2"></span>
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Staff Contacts */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            ჩვენი გუნდი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* ვანო ხარაზიშვილი - დირექტორი */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <UserIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                ვანო ხარაზიშვილი
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">დირექტორი</p>
              <div className="space-y-2">
                <a 
                  href="tel:591992225" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                 +995 591 99 22 25
                </a>
                <a 
                  href="mailto:director@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  director@fciveria.ge
                </a>
              </div>
            </div>

            {/* გია აბელიანი - გულშემატკივრების და შშმ პირების მომსახურება */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <UserIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                გია აბელიანი
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">გულშემატკივრების და შშმ პირების მომსახურება</p>
              <div className="space-y-2">
                <a 
                  href="tel:557909826" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                 +995 557 90 98 26
                </a>
                <a 
                  href="mailto:fans@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  fans@fciveria.ge
                </a>
              </div>
            </div>

            {/* პაატა ქიმერიძე - სოციალური პასუხისმგებლობის ოფიცერი */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <UserIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                პაატა ქიმერიძე
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">სოციალური პასუხისმგებლობის ოფიცერი</p>
              <div className="space-y-2">
                <a 
                  href="tel:599747010" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                  +995 599 74 70 10
                </a>
                <a 
                  href="mailto:social@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  social@fciveria.ge
                </a>
              </div>
            </div>

            {/* ვახტანგ ჯაფარაშვილი - მატჩების მენეჯერი */}
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <UserIcon className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                ვახტანგ ჯაფარაშვილი
              </h3>
              <p className="text-sm text-primary-600 font-georgian mb-3">მატჩების მენეჯერი</p>
              <div className="space-y-2">
                <a 
                  href="tel:+995551551185" 
                  className="block text-gray-600 font-georgian text-sm hover:text-primary-600 transition-colors"
                >
                  +995 551 55 11 85
                </a>
                <a 
                  href="mailto:matches@fciveria.ge" 
                  className="block text-blue-600 font-georgian text-sm hover:text-blue-800 transition-colors"
                >
                  matches@fciveria.ge
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
          მისამართი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-4">
           
              </h3>
              <p className="text-gray-600 font-georgian">
                წმინდა ნინოს N1
              </p>
            </div>
          </div>
        </div>

        {/* Organizational Chart */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            ორგანიზაციული სტრუქტურა
          </h2>
          <div className="text-center">
            <img 
              src="/sqema.png" 
              alt="FC ივერია - ორგანიზაციული სტრუქტურა" 
              className="max-w-full h-auto mx-auto rounded-lg shadow-md"
              style={{ maxHeight: '600px' }}
            />
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.imageUrl}
        alt={selectedImage?.alt}
        title={selectedImage?.title}
        subtitle={selectedImage?.subtitle}
      />
    </div>
  );
};

export default Administration;
